import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useTenant } from "@/hooks/useTenant";
import { validateImageFiles } from "@/lib/validation";
import { toast } from "sonner";
import type { TrailerPhotoType } from "@/components/yard/yardPhotoUtils";

/**
 * Driver-initiated Trailer / License Plate photo capture from the
 * assignment readiness checklist. Writes to the private `trailer-images`
 * bucket and inserts a `trailer_event_photos` row per file. Because
 * `trailer_event_photos.trailer_event_id` is NOT NULL, we first insert a
 * lightweight `driver_verified` `trailer_events` row (marked in `notes`
 * so it is NOT counted as a full verification — the checklist filters on
 * `truck_id IS NOT NULL`).
 */
/** Storage/RLS refusals surface as 403 / "row-level security" / "Unauthorized". */
function isPermissionError(err: unknown): boolean {
  const e = err as { statusCode?: string | number; status?: number; message?: string } | null;
  const code = String(e?.statusCode ?? e?.status ?? "");
  const msg = (e?.message ?? "").toLowerCase();
  return (
    code === "403" ||
    code === "401" ||
    msg.includes("row-level security") ||
    msg.includes("violates row level security") ||
    msg.includes("unauthorized") ||
    msg.includes("not authorized")
  );
}

export function useTrailerReadinessPhotoUpload(params: {
  trailerIdText: string | null | undefined;
  photoType: Extract<TrailerPhotoType, "trailer" | "license_plate">;
  /**
   * Optional storage-path prefix override. When set, uploaded files use
   * `event/{id}/{variant}-…` in the storage path (instead of the raw
   * photoType), letting the same photo_type back multiple checklist rows
   * — e.g. `trailer-left` and `trailer-right` sharing `photo_type = "trailer"`.
   */
  variant?: string;
  /** Leg the photos are for; stamped on the stub event (DTP-110609). */
  legId?: string | null;
}) {
  const { trailerIdText, photoType, variant, legId } = params;
  const keyDiscriminator = variant ?? photoType;
  const [isUploading, setIsUploading] = useState(false);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { tenantId } = useTenant();

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["trailer-event-photos-count", trailerIdText, keyDiscriminator],
    });
    queryClient.invalidateQueries({
      queryKey: ["trailer-event-photos-list", trailerIdText, keyDiscriminator],
    });
  }, [queryClient, trailerIdText, keyDiscriminator]);

  const uploadFiles = useCallback(
    async (files: FileList | File[]) => {
      if (!trailerIdText || !tenantId) {
        toast.error("Missing trailer or tenant context");
        return { uploaded: 0, failed: 0 };
      }
      const { valid, errors } = validateImageFiles(files);
      errors.forEach((e) => toast.error(e));
      if (valid.length === 0) return { uploaded: 0, failed: 0 };

      // Resolve trailer db id.
      const { data: trailerRow, error: trailerErr } = await supabase
        .from("trailers")
        .select("id")
        .eq("trailer_id", trailerIdText)
        .eq("tenant_id", tenantId)
        .maybeSingle();
      if (trailerErr || !trailerRow) {
        toast.error("Trailer not found");
        return { uploaded: 0, failed: 0 };
      }

      setIsUploading(true);
      let uploaded = 0;
      let failed = 0;
      try {
        // One stub event per batch — links every photo in this upload.
        const eventPayload: Record<string, unknown> = {
          trailer_id: trailerRow.id,
          event_type: "driver_verified",
          tenant_id: tenantId,
          created_by_user_id: user?.id ?? null,
          notes: `Driver ${variant ?? photoType} photo upload`,
          ...(legId ? { leg_id: legId } : {}),
        };
        const { data: evt, error: evtErr } = await supabase
          .from("trailer_events")
          .insert(eventPayload as never)
          .select("id")
          .single();
        if (evtErr || !evt?.id) throw evtErr ?? new Error("Event insert failed");
        const eventId = evt.id;

        for (const file of valid) {
          try {
            const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
            const rand = Math.random().toString(36).substring(7);
            const pathPrefix = variant || photoType;
            const path = `event/${eventId}/${pathPrefix}-${Date.now()}-${rand}.${ext}`;
            const { error: upErr } = await supabase.storage
              .from("trailer-images")
              .upload(path, file, {
                cacheControl: "3600",
                upsert: false,
                contentType: file.type || undefined,
              });
            if (upErr) throw upErr;

            const photoPayload: Record<string, unknown> = {
              trailer_event_id: eventId,
              trailer_id: trailerRow.id,
              photo_type: photoType,
              image_path: path,
              uploaded_by: user?.id ?? null,
              tenant_id: tenantId,
            };
            const { error: insErr } = await supabase
              .from("trailer_event_photos" as never)
              .insert(photoPayload as never);
            if (insErr) {
              await supabase.storage
                .from("trailer-images")
                .remove([path])
                .catch(() => undefined);
              throw insErr;
            }
            uploaded++;
          } catch (perFileErr) {
            failed++;
            console.error(`Trailer ${variant ?? photoType} upload failed`, perFileErr);
            toast.error(
              isPermissionError(perFileErr)
                ? `${file.name} was rejected — you don't have permission to add photos for this trailer. Please report this to dispatch.`
                : `Failed to upload ${file.name}`,
            );
          }
        }

        // Don't leave an empty verification record behind when nothing saved.
        if (uploaded === 0) {
          await supabase.from("trailer_events").delete().eq("id", eventId);
        }


        if (uploaded > 0) {
          const label =
            variant === "trailer-left"
              ? "left-side trailer"
              : variant === "trailer-right"
                ? "right-side trailer"
                : photoType === "trailer"
                  ? "trailer"
                  : "license plate";
          toast.success(
            `${uploaded} ${label} photo${uploaded > 1 ? "s" : ""} uploaded`,
          );
          invalidate();
        }
      } catch (err) {
        console.error("Trailer photo batch failed", err);
        toast.error("Upload failed");
      } finally {
        setIsUploading(false);
      }
      return { uploaded, failed };
    },
    [trailerIdText, tenantId, user?.id, photoType, variant, legId, invalidate],
  );

  return { uploadFiles, isUploading };
}